<script lang="ts" setup>
  import { BngProductSkeletonCard } from '~/components/golf'
  import { LocationType, type Nullable } from '~/core/types'
  import type { GolfLite } from '~/core/types/shared'
  import { Sort } from '~/core/types/Sort.enum'
  import { BngContentFit, BngModal } from '~/layouts/components'

  import NoResult from './NoResult.vue'
  import ResultHead from './ResultHead.vue'

  const props = defineProps<{
    golfs: GolfLite[]
    isFetchingGolfs?: boolean
    locationType: LocationType
  }>()

  const emit = defineEmits<{
    (e: 'displayAll' | 'changeFilter'): void
  }>()

  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)

  const hoveredCardId = ref<Nullable<number>>(null)

  const sortType = ref(Sort.ASC_ALPHA)
  const mapModal = ref(false)
  const showMap = ref(true)

  function handleMapModalDisplay() {
    mapModal.value = true
  }

  function closeMapModal() {
    mapModal.value = false
  }

  function handleCardHover(id: number) {
    hoveredCardId.value = id
  }

  const sortTypes = computed(() => {
    switch (props.locationType) {
    case LocationType.DEPARTMENT:
    case LocationType.REGION:
    case LocationType.COUNTRY:
      return [Sort.DESC_PRICE, Sort.ASC_PRICE, Sort.DESC_RATING]
    case LocationType.PROXIMITY:
    case LocationType.CITY:
      return [Sort.DESC_PRICE, Sort.ASC_PRICE, Sort.DESC_RATING, Sort.ASC_DISTANCE]
    case LocationType.GOLF:
      return []
    default:
      return []
    }
  })

  const golfResults = computed((): GolfLite[] => {
    switch (sortType.value) {
    case Sort.ASC_ALPHA:
      return props.golfs.toSorted((a, b) => a.name.localeCompare(b.name))
    case Sort.DESC_ALPHA:
      return props.golfs.toSorted((a, b) => b.name.localeCompare(a.name))
    case Sort.ASC_PRICE:
      return props.golfs.toSorted((a, b) => a.best_price - b.best_price)
    case Sort.DESC_PRICE:
      return props.golfs.toSorted((a, b) => b.best_price - a.best_price)
    case Sort.ASC_RATING:
      return props.golfs.toSorted((a, b) => (a.rating ?? 0) - (b.rating ?? 0))
    case Sort.DESC_RATING:
      return props.golfs.toSorted((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
    case Sort.ASC_DISTANCE:
      return props.golfs.toSorted((a, b) => (a.distance || 0) - (b.distance || 0))
    case Sort.DESC_DISTANCE:
      return props.golfs.toSorted((a, b) => (b.distance || 0) - (a.distance || 0))
    default:
      return []
    }
  })

  function updateSort() {
    if ([LocationType.PROXIMITY, LocationType.CITY].includes(props.locationType)) {
      sortType.value = Sort.ASC_DISTANCE
    } else {
      sortType.value = Sort.DESC_RATING
    }
  }

  watch(() => props.golfs, () => {
    updateSort()
  }, { immediate: true })

  watch(() => props.locationType, (l) => {
    if (l === LocationType.REGION) {
      sortType.value = Sort.DESC_RATING
    }
  })

</script>
<template>
  <div
    class="c-results-list green-fee"
  >
    <ClientOnly>
      <Teleport
        v-if="mapModal"
        to="#modal"
      >
        <BngModal
          closable
          @close="closeMapModal"
        >
          <ResultsMap
            display-price
            :items="props.golfs"
            is-green-fee
          />
        </BngModal>
      </Teleport>
    </ClientOnly>
    <BngContentFit>
      <ResultHead
        v-model:sort-type="sortType"
        v-model:show-map="showMap"
        :count="golfResults.length"
        :sort-types="sortTypes"
      />
      <div
        class="content"
        :class="{ 'map-displayed': showMap }"
      >
        <div class="results">
          <template v-if="!isFetchingGolfs">
            <Paper
              v-for="(result, index) in golfResults"
              :key="result.id + '-' + index"
              class="hover-shadow"
            >
              <GolfCardWrapper
                :golf="result"
                departures
                @mouseover="handleCardHover(result.id)"
              />
            </Paper>
          </template>
          <template v-else>
            <Paper
              v-for="skeleton in 4"
              :key="skeleton"
              class="flat"
            >
              <BngProductSkeletonCard is-green-fee />
            </Paper>
          </template>
        </div>
        <ClientOnly>
          <div
            v-if="matchDesktop && showMap && golfResults.length > 0"
            class="map-container"
          >
            <ResultsMap
              is-green-fee
              :items="props.golfs"
              :focused="hoveredCardId"
              display-price
            />
          </div>
        </ClientOnly>
      </div>
      <template v-if="!golfResults.length">
        <NoResult
          :display-filter-link="true"
          @display-all="emit('displayAll')"
          @change-filter="emit('changeFilter')"
        />
      </template>
      <button
        v-if="golfResults.length > 0"
        class="map-btn"
        @click="handleMapModalDisplay"
      >
        <SvgIcon
          icon="map"
          :width="32"
          :height="32"
        />
      </button>
    </BngContentFit>
  </div>
</template>
<style scoped>

.c-results-list {
  display: flex;
  flex-direction: column;
  background: white;

  .lc-content-fit {
    z-index: 1;
  }

  .content {
    display: flex;
    flex: 1 0 auto;
    flex-direction: row;
    margin-top: 1.5em;

    .results {
      display: flex;
      max-width: 100%;
      flex: 1 1 auto;
      flex-direction: column;

      .c-paper {
        overflow: hidden;
        padding: 0;
        margin: 0 0 1em;

        .card {
          flex: 1 0 auto;
        }
      }
    }

    .map-container {
      position: sticky;
      top: 0;
      flex: 0 0 auto;
      transition: top 0.3s ease-out;
    }
  }

  .map-btn {
    position: sticky;
    z-index: 10;
    bottom: calc(55px + 0.625em);
    left: calc(50% - 1.75em);
    display: block;
    width: 3.5em;
    height: 3.5em;
    border-radius: 50%;
    background-color: #fff;
    box-shadow: 0 0 20px 0 rgb(26 44 54 / 10%);

    .c-icon {
      color: var(--color-secondary);
    }
  }

  .c-no-result {
    flex: 1 0 auto;
  }

  @media screen and (width >= 600px) {
    .content {
      .results {
        display: grid;
        grid-gap: 1.5em;
        grid-template-columns: 1fr 1fr;
      }
    }
  }

  @media screen and (width >= 1024px) {
    --menu-height: 90px;

    .c-loader {
      height: calc(100vh - (var(--menu-height) + var(--search-height)));
    }

    .content {
      .results {
        display: grid;
        flex: 0 0 100%;
        align-items: baseline;
        grid-auto-rows: min-content;
        grid-gap: 1.5em;
        grid-template-columns: 1fr;

        .c-paper {
          padding: 1.5em;
          margin: 0;

          .card {
            flex: 1 1 100%;
          }
        }
      }

      .map-container {
        position: sticky;
        top: calc(90px + var(--search-height));
        height: calc(100vh - (var(--menu-height) + var(--search-height) + 90px));
        flex: 0 0 50%;
        padding-left: 0.75em;
      }

      &.map-displayed {
        margin-top: 0;

        .results {
          max-width: 50%;
          flex: 0 0 50%;
          padding-top: 1.5em;
          padding-right: 0.75em;
          grid-template-columns: 1fr;
        }
      }
    }

    .map-btn {
      display: none;
    }
  }

  @media screen and (width >= 1280px) {
    .content {
      .results {
        grid-template-columns: 1fr 1fr;
      }
    }
  }
}
</style>
